import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import { formatPriceFR } from '../../utils/priceUtils';
import type { Chiffrage } from '../Chiffrage/chiffrageTypes';
// @ts-ignore
const logoCentralis = require('../../assets/logo/logo-centralis-gris.png');

// Devis "chiffrage" : calque allege de OffrePDF (meme charte, meme structure de tableau). Rend
// l'entete prestataire, la liste des articles avec prix resolus, le total HT et le code en evidence.

/** Une ligne du tableau, prix deja resolus par l'appelant (le PDF ne calcule aucun prix). */
export interface ChiffragePDFArticle {
  code: string;
  nom: string;
  quantite: number;
  prixUnitaire: number;
  prixTotal: number;
  unite: string;
}

/** Entete prestataire : nom de l'entite + numero de contrat quand ils sont connus. */
export interface ChiffragePDFEntete {
  nom?: string;
  numeroContrat?: string;
  siret?: string;
  adresse?: string;
}

export interface ChiffragePDFProps {
  chiffrage: Chiffrage;
  articles: ChiffragePDFArticle[];
  entete: ChiffragePDFEntete;
  total: number;
  code: string;
}

const styles = StyleSheet.create({
  page: {
    padding: 0,
    fontSize: 12,
    fontFamily: 'Helvetica',
    backgroundColor: '#FFFDFC',
    color: '#374151',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
    paddingBottom: 16,
  },
  logo: {
    width: 100,
  },
  headerRight: {
    alignItems: 'flex-end',
  },
  documentTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#595959',
  },
  dateInfo: {
    fontSize: 11,
    color: '#6B7280',
    marginTop: 4,
  },
  enteteSection: {
    padding: 16,
    marginHorizontal: 24,
    marginBottom: 16,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  enteteNom: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#595959',
    marginBottom: 4,
  },
  enteteLine: {
    fontSize: 11,
    marginBottom: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#595959',
    backgroundColor: 'rgba(215, 217, 217, 0.25)',
    padding: 12,
    marginHorizontal: 24,
    marginTop: 8,
    marginBottom: 8,
    borderRadius: 8,
  },
  infoRow: {
    flexDirection: 'row',
    marginHorizontal: 24,
    marginBottom: 6,
  },
  infoLabel: {
    fontWeight: 'bold',
    width: '35%',
    fontSize: 11,
  },
  infoValue: {
    flex: 1,
    fontSize: 11,
  },
  table: {
    marginHorizontal: 24,
    marginTop: 12,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 24,
    alignItems: 'center',
  },
  tableHeader: {
    backgroundColor: 'rgba(215, 217, 217, 0.25)',
    fontWeight: 'bold',
  },
  cellCode: {
    width: 70,
    fontSize: 10,
    padding: 6,
  },
  cellNom: {
    flex: 1,
    fontSize: 10,
    padding: 6,
  },
  cellUnite: {
    width: 55,
    fontSize: 10,
    padding: 6,
  },
  cellNum: {
    width: 45,
    fontSize: 10,
    padding: 6,
    textAlign: 'right',
  },
  cellPrix: {
    width: 75,
    fontSize: 10,
    padding: 6,
    textAlign: 'right',
  },
  totalSection: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginHorizontal: 24,
    marginTop: 12,
    paddingTop: 8,
    borderTopWidth: 2,
    borderTopColor: '#E5E7EB',
  },
  totalLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    marginRight: 8,
  },
  totalValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#595959',
  },
  codeSection: {
    marginHorizontal: 24,
    marginTop: 24,
    padding: 16,
    borderWidth: 2,
    borderColor: '#f97316',
    borderRadius: 8,
    backgroundColor: '#FFF7ED',
    alignItems: 'center',
  },
  codeLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#595959',
    marginBottom: 6,
  },
  codeValue: {
    fontSize: 28,
    fontWeight: 'bold',
    fontFamily: 'Courier',
    color: '#f97316',
    letterSpacing: 4,
  },
  codeHint: {
    fontSize: 9,
    color: '#6B7280',
    marginTop: 6,
    textAlign: 'center',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 24,
    right: 24,
    fontSize: 10,
    color: '#6B7280',
    textAlign: 'center',
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
});

const formatDateFR = (): string => new Date().toLocaleDateString('fr-FR');

const ChiffragePDF: React.FC<ChiffragePDFProps> = ({ chiffrage, articles, entete, total, code }) => {
  const today = formatDateFR();

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* En-tete : logo + titre du document */}
        <View style={styles.header} wrap={false}>
          <Image src={logoCentralis} style={styles.logo} />
          <View style={styles.headerRight}>
            <Text style={styles.documentTitle}>Devis</Text>
            <Text style={styles.dateInfo}>Document généré le {today}</Text>
          </View>
        </View>

        {/* Entete prestataire */}
        <View style={styles.enteteSection} wrap={false}>
          <Text style={styles.enteteNom}>{entete.nom || 'Prestataire'}</Text>
          {entete.numeroContrat ? (
            <Text style={styles.enteteLine}>Contrat N° {entete.numeroContrat}</Text>
          ) : null}
          {entete.siret ? <Text style={styles.enteteLine}>SIRET : {entete.siret}</Text> : null}
          {entete.adresse ? <Text style={styles.enteteLine}>{entete.adresse}</Text> : null}
        </View>

        {/* Rappel du perimetre du chiffrage */}
        <Text style={styles.sectionTitle}>Chiffrage</Text>
        {chiffrage.libelle ? (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Libellé :</Text>
            <Text style={styles.infoValue}>{chiffrage.libelle}</Text>
          </View>
        ) : null}
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Rang :</Text>
          <Text style={styles.infoValue}>{chiffrage.rang}</Text>
        </View>

        {/* Tableau des articles (prix resolus par l'appelant) */}
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={styles.cellCode}>Code</Text>
            <Text style={styles.cellNom}>Article</Text>
            <Text style={styles.cellUnite}>Unité</Text>
            <Text style={styles.cellNum}>Qté</Text>
            <Text style={styles.cellPrix}>Prix unitaire</Text>
            <Text style={styles.cellPrix}>Prix total</Text>
          </View>

          {articles.length > 0 ? (
            articles.map((article, index) => (
              <View key={`article-${index}`} style={styles.tableRow} wrap={false}>
                <Text style={styles.cellCode}>{article.code || '-'}</Text>
                <Text style={styles.cellNom}>{article.nom || '-'}</Text>
                <Text style={styles.cellUnite}>{article.unite || '-'}</Text>
                <Text style={styles.cellNum}>{article.quantite}</Text>
                <Text style={styles.cellPrix}>{formatPriceFR(article.prixUnitaire)}</Text>
                <Text style={styles.cellPrix}>{formatPriceFR(article.prixTotal)}</Text>
              </View>
            ))
          ) : (
            <View style={styles.tableRow}>
              <Text style={[styles.cellNom, { textAlign: 'center' }]}>Aucun article</Text>
            </View>
          )}
        </View>

        {/* Total HT */}
        <View style={styles.totalSection}>
          <Text style={styles.totalLabel}>Total HT :</Text>
          <Text style={styles.totalValue}>{formatPriceFR(total)}</Text>
        </View>

        {/* Code du chiffrage en evidence */}
        <View style={styles.codeSection} wrap={false}>
          <Text style={styles.codeLabel}>Code du chiffrage</Text>
          <Text style={styles.codeValue}>{code}</Text>
          <Text style={styles.codeHint}>
            Communiquez ce code à votre acheteur pour qu'il récupère ce chiffrage.
          </Text>
        </View>

        {/* Pied de page */}
        <Text
          style={styles.footer}
          fixed
          render={({ pageNumber, totalPages }) =>
            `Document généré par Centralis • ${today} • Page ${pageNumber}/${totalPages}`
          }
        />
      </Page>
    </Document>
  );
};

export default ChiffragePDF;
